import {
    Box,
    Button,
    Drawer,
    IconButton,
    MenuItem,
    Select,
} from '@mui/material'
import useInfo from 'hooks/useInfo/useInfo'
import { useAppSelector } from 'store/useStoreHook'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import HelpDocument from 'components/HelpDocument/HelpDocument'
import { menuHelpWidth } from 'router/layouts/AdminLayout'
import { useCallback, useEffect, useState } from 'react'
import { Category } from 'hooks/useHelpDocument/useHelpDocument'
import HelpSupport from 'components/HelpSupport/HelpSupport'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import HelpDocumentContextProvider, {
    useHelpDocumentContext,
} from 'components/HelpDocument/HelpDocumentContext'
import replaceDocumentRoute from 'pages/DocumentPage/helper/replaceDocumentRoute'

type modeType = 'docs' | 'help'
const docModeOptions: { label: string; mode: Category }[] = [
    {
        label: 'General',
        mode: 'General',
    },
    {
        label: 'Quick Service',
        mode: 'quick service',
    },
    {
        label: 'Table Service',
        mode: 'table service',
    },
    {
        label: 'KDS',
        mode: 'kds',
    },
]

export default function SideHelpDocument() {
    return (
        <HelpDocumentContextProvider>
            <SideHelpDocumentWrapper />
        </HelpDocumentContextProvider>
    )
}

export function SideHelpDocumentWrapper() {
    const isOpen = useAppSelector((state) => state.ui.helpDocument.isOpen)
    const { toggleHelpDoc } = useInfo()
    const { IsSmallerThanSpecified } = useScreenSize(1299)
    const {
        allDocuments,
        selectCategory,
        onSelectCategory,
        currentDocument,
    } = useHelpDocumentContext()

    const [selectModeType, setSelectedModeType] = useState<modeType>('docs')

    const openNewTab = useCallback(() => {
        const { category, title } = allDocuments.find(
            (d) => d.id === currentDocument?.id,
        )!
        window.open(replaceDocumentRoute(category, title), '_blank')
    }, [allDocuments, currentDocument?.id])

    useEffect(() => {
        if (!isOpen) setSelectedModeType('docs')
    }, [isOpen])

    return (
        <Drawer variant="persistent" anchor="right" open={isOpen}>
            <Box sx={{ position: 'relative' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'fixed',
                        width: menuHelpWidth,
                        zIndex: 99,
                    }}
                >
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                            backgroundColor: 'primary.main',
                            padding: 1.25,
                        }}
                    >
                        <Box
                            display="flex"
                            sx={{ color: 'primary.contrastText' }}
                            alignItems="center"
                        >
                            <IconButton
                                onClick={toggleHelpDoc}
                                sx={{ color: 'primary.contrastText' }}
                            >
                                <ChevronRightIcon />
                            </IconButton>
                            <Button
                                fullWidth
                                variant={
                                    selectModeType === 'docs'
                                        ? 'contained'
                                        : 'outlined'
                                }
                                color="invertPrimary"
                                onClick={() => setSelectedModeType('docs')}
                            >
                                Docs
                            </Button>
                            <Button
                                fullWidth
                                variant={
                                    selectModeType === 'help'
                                        ? 'contained'
                                        : 'outlined'
                                }
                                color="invertPrimary"
                                sx={{
                                    marginLeft: 1,
                                }}
                                onClick={() => setSelectedModeType('help')}
                            >
                                Help
                            </Button>
                        </Box>
                        {selectModeType === 'docs' && (
                            <Box display="flex">
                                <Button
                                    variant="text"
                                    size="small"
                                    sx={{
                                        margin: 1,
                                        color: 'primary.contrastText',
                                    }}
                                    endIcon={<OpenInNewIcon />}
                                    onClick={() => openNewTab()}
                                >
                                    {!IsSmallerThanSpecified && (
                                        <>Open new tab</>
                                    )}
                                </Button>
                                <Select
                                    size="small"
                                    value={selectCategory}
                                    onChange={(e) => {
                                        e.stopPropagation()
                                        onSelectCategory(
                                            e.target.value as Category,
                                        )
                                    }}
                                    variant="outlined"
                                    sx={{
                                        '&.MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor:
                                                    'primary.contrastText',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'primary.light',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'primary.light',
                                            },
                                        },
                                        color: 'primary.contrastText',
                                    }}
                                >
                                    {docModeOptions.map((option, index) => (
                                        <MenuItem
                                            key={index}
                                            value={option.mode}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        )}
                    </Box>
                </Box>

                <Box width={menuHelpWidth}>
                    {selectModeType === 'docs' ? (
                        <HelpDocument />
                    ) : selectModeType === 'help' ? (
                        <HelpSupport />
                    ) : (
                        <></>
                    )}
                </Box>
            </Box>
        </Drawer>
    )
}
